import { shallowEqual, useSelector } from 'react-redux';
import { Store } from 'redux';

import { getCurrentProcessPath } from '../Parser/bindings';
import {
  currentDialogGraphSelector,
  currentGraphSelector,
  dialogProcessIdSelector,
  GetCurrentGraphResult,
  graphPathSelector,
  originalGraphSelector,
  rootPathSelector
} from './selectors';
import { BaseState, CommonGraph, Metadata } from './types';

export function useOriginalGraph<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  return useSelector<S, GetCurrentGraphResult<G>>(originalGraphSelector, shallowEqual);
}

export function useCurrentGraph<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  return useSelector<S, GetCurrentGraphResult<G>>((state) => {
    return currentGraphSelector(state);
  }, shallowEqual);
}

export function getCurrentGraphFromStore<G extends CommonGraph, S extends BaseState<G, Metadata>>(store: Store<S>) {
  return currentGraphSelector(store.getState());
}

export function useCurrentGraphPath<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  const componentId = useSelector<S, string | undefined>(dialogProcessIdSelector);
  const rootPath = useSelector<S, string>(rootPathSelector);
  const graphPath = useSelector<S, string>(graphPathSelector);
  return {
    rootPath,
    graphPath,
    componentId
  };
}

export function useCurrentDialogGraph<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  return useSelector<S, GetCurrentGraphResult<G>>(currentDialogGraphSelector, shallowEqual);
}

export function useDialogProcessPath<G extends CommonGraph, S extends BaseState<G, Metadata>>() {
  const graphPath = useSelector<S, string>(graphPathSelector);
  const componentId = useSelector<S, string | undefined>(dialogProcessIdSelector);
  if (!componentId) {
    return undefined;
  }
  const processPath = getCurrentProcessPath(componentId, graphPath);
  return processPath;
}
